export type StrategyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'accent'; text: string }
  | { kind: 'sub'; text: string }
  | { kind: 'list'; items: string[] }

export interface StrategySection {
  number: string
  title: string
  blocks: StrategyBlock[]
}

export const strategyMeta = {
  title: 'Campaign Strategy — Precision Phenotyping in DME',
  subtitle: 'Scientific-first engagement strategy for Denmark',
  note: 'Internal document. Not part of the HCP-facing journey and not counted as an HCP interaction.',
  coreIdea: 'Earn the treatment conversation through precision phenotyping.',
  coreSupport:
    'By beginning with independent scientific value rather than treatment promotion, the campaign seeks to build credibility with a highly selective specialist audience and create the foundation for a later discussion about inflammatory DME and more individualised clinical decision-making.',
  finalLine:
    'Before we talk about how DME is treated, we first need to look more precisely at what type of DME we are treating.',
}

export const strategySections: StrategySection[] = [
  {
    number: '01',
    title: 'Market context',
    blocks: [
      {
        kind: 'p',
        text: 'The Danish ophthalmology environment presents a particular engagement challenge. The specialist audience is small, highly scientific and selective in how it engages with pharmaceutical companies.',
      },
      {
        kind: 'p',
        text: 'Traditional promotional communication therefore risks being dismissed before the message has established sufficient clinical relevance.',
      },
      {
        kind: 'p',
        text: 'At the same time, treatment decisions in DME can involve meaningful patient burden. Where the ideal candidate is difficult to identify with confidence, clinicians may understandably be cautious about exposing patients to additional intervention without a sufficiently clear rationale.',
      },
      {
        kind: 'p',
        text: 'The communication challenge is therefore broader than product awareness. The first task is not to persuade ophthalmologists to consider a treatment. The first task is to create confidence that:',
      },
      { kind: 'accent', text: 'DME can be characterised more precisely.' },
    ],
  },
  {
    number: '02',
    title: 'Strategic opportunity',
    blocks: [
      {
        kind: 'p',
        text: 'Recent AI-assisted OCT research creates an opportunity to begin the conversation at a different point: diagnostic precision before treatment discussion.',
      },
      {
        kind: 'p',
        text: 'Rather than beginning with therapy, the campaign begins with OCT-based phenotyping. The Midena analysis demonstrates that familiar OCT variables can be analysed together to identify distinct morphological DME phenotypes.',
      },
      { kind: 'accent', text: 'From measuring fluid to recognising phenotype.' },
      {
        kind: 'p',
        text: 'The implication is not that a phenotype automatically determines treatment. Instead, the campaign introduces the idea that more precise treatment decisions begin with more precise characterisation of the patient.',
      },
    ],
  },
  {
    number: '03',
    title: 'Why the campaign is phased',
    blocks: [
      {
        kind: 'p',
        text: 'The campaign is deliberately divided into stages. The audience should first be given a reason to reconsider how DME is characterised before being asked to reconsider how it may be treated.',
      },
      { kind: 'sub', text: 'Phase 1 — Precision phenotyping' },
      {
        kind: 'p',
        text: 'Completely unbranded and purely scientific. Focus on OCT, AI-assisted analysis, DME heterogeneity, morphological phenotyping and precision diagnostics.',
      },
      {
        kind: 'p',
        text: 'Out of scope: Ozurdex, dexamethasone, implants, treatment switching, treatment superiority and specific treatment pathways.',
      },
      {
        kind: 'p',
        text: 'The objective is to establish that DME is not one morphological pattern. The desired response is: “Perhaps there is more information in the OCT than fluid volume alone.”',
      },
      { kind: 'sub', text: 'Phase 2 — Understanding inflammatory DME' },
      {
        kind: 'p',
        text: 'Once phenotype-based characterisation is established, the science can deepen: inflammatory characteristics within DME, OCT biomarker patterns, inflammatory and structural differences between patients, expert interpretation and recognition of appropriate patient types.',
      },
      {
        kind: 'p',
        text: 'The question progresses from “Can we phenotype DME more precisely?” to “What might these phenotypes tell us about underlying disease biology?”',
      },
      { kind: 'sub', text: 'Phase 3 — Clinical decision-making' },
      {
        kind: 'p',
        text: 'Only once the scientific foundation is established should communication move toward treatment: precision imaging → precision phenotyping → informed treatment decisions.',
      },
      {
        kind: 'p',
        text: 'Avoid implying that the Midena phenotype model itself dictates treatment.',
      },
    ],
  },
  {
    number: '04',
    title: 'Why this is an unusual teaser',
    blocks: [
      {
        kind: 'p',
        text: 'A conventional teaser is designed to generate awareness → curiosity → action. This teaser has a different strategic role.',
      },
      { kind: 'accent', text: 'Credibility → scientific curiosity → permission for the next conversation.' },
      {
        kind: 'p',
        text: 'The primary reward is scientific insight, not a product message. The teaser is closer to a short scientific thought-starter than a promotional communication. Its job is to:',
      },
      {
        kind: 'list',
        items: [
          'Earn attention',
          'Establish credibility',
          'Introduce a genuinely new scientific perspective',
          'Reduce resistance to future communication',
          'Create curiosity around inflammatory DME',
          'Prepare the audience for deeper educational content',
        ],
      },
      {
        kind: 'p',
        text: 'The absence of a product message is intentional. It is the strategy.',
      },
    ],
  },
  {
    number: '05',
    title: 'The 5-page story',
    blocks: [
      { kind: 'sub', text: 'Page 1 — Reframe the OCT' },
      {
        kind: 'p',
        text: '“What if OCT could reveal phenotype — not just fluid?” Introduces the possibility that OCT may provide a richer morphological picture of DME, with the Midena data establishing immediate credibility. Strategic role: challenge the frame without criticising current practice.',
      },
      { kind: 'sub', text: 'Page 2 — Establish heterogeneity' },
      {
        kind: 'p',
        text: '“DME is not one morphological pattern.” Connects established clinical heterogeneity with the possibility of systematic phenotyping. Takeaway: same diagnosis, different phenotype.',
      },
      { kind: 'sub', text: 'Page 3 — Explain the inputs' },
      {
        kind: 'p',
        text: '“5 OCT variables. One clustering model.” Shows the phenotypes are based on familiar clinical variables — IRF · SRF · I-HRF · ELM · EZ — without teaching basic OCT anatomy. Strategic role: make the AI output clinically credible rather than opaque.',
      },
      { kind: 'sub', text: 'Page 4 — Deliver the reveal' },
      {
        kind: 'p',
        text: '“One diagnosis. Four phenotypes.” Shows the four morphological clusters identified through the combined OCT biomarker profiles, turning heterogeneity into something visually recognisable. They are morphological phenotype clusters — not treatment groups, treatment-response categories or validated disease stages.',
      },
      { kind: 'sub', text: 'Page 5 — Deeper scientific engagement' },
      {
        kind: 'p',
        text: '“Let’s look at a few together.” Converts scientific curiosity into a low-friction next step — book a phenotyping walkthrough. The next action remains scientific rather than promotional: bring your own scans, explore OCT-based phenotyping, discuss biomarker patterns, engage at your own pace.',
      },
    ],
  },
  {
    number: '06',
    title: 'The hidden quiz layer',
    blocks: [
      {
        kind: 'p',
        text: 'The hidden quiz and answer pages are an optional engagement layer, not the conceptual backbone of the Snak. Their role is to provide optional interaction, peer comparison and an additional engagement signal.',
      },
      {
        kind: 'p',
        text: 'Users who prefer a direct scientific journey are never forced through quiz mechanics.',
      },
    ],
  },
  {
    number: '07',
    title: 'Why this approach fits the audience',
    blocks: [
      {
        kind: 'accent',
        text: 'For a small, highly specialised audience, credibility is more valuable than promotional intensity.',
      },
      {
        kind: 'p',
        text: 'Instead of asking the audience to believe a treatment claim, the first interaction asks them to consider published OCT evidence. Instead of beginning with a product, it begins with a clinical question. Instead of telling ophthalmologists how to treat, it gives them a new way to think about how DME may be characterised.',
      },
      {
        kind: 'p',
        text: 'The campaign must first demonstrate: “This is worth my attention as a clinician.” Only later should it ask: “What could this mean for treatment?”',
      },
    ],
  },
  {
    number: '08',
    title: 'Mapping to the HCP archetypes',
    blocks: [
      {
        kind: 'p',
        text: 'The adoption model segments by audience maturity, not by time: Foundational, Developing and Advanced specialists exist simultaneously. Each instalment is therefore written for one archetype and carries two layers — a market-shaping spine and a short differentiation tail.',
      },
      {
        kind: 'list',
        items: [
          'Snak 1 · Foundational · Spark precision · differentiation: Preserve · completely unbranded',
          'Snak 2 · Developing · Embed precision · differentiation: Prevent · brand colours, no logo',
          'Snak 3 · Advanced · Ignite precision · differentiation: Predict · fully branded',
        ],
      },
      {
        kind: 'accent',
        text: 'Branding is not a slow reveal over time. It is a layer that each archetype carries differently.',
      },
      {
        kind: 'p',
        text: 'Sequence remains available but is no longer the only path. An entry menu lets a specialist enter at their own archetype, or lets a representative run all three instalments as one face-to-face presentation.',
      },
    ],
  },
  {
    number: '09',
    title: 'Snak 2 — Developing · Embed precision',
    blocks: [
      {
        kind: 'p',
        text: 'This reader already accepts that phenotypes exist. Persuasion is finished; the task is to add the time axis and make phenotyping part of the routine.',
      },
      { kind: 'sub', text: 'Page 1 — The window of opportunity' },
      {
        kind: 'p',
        text: '“The window closes quietly.” Outer retinal integrity declines through sustained exposure while acuity still looks acceptable, so structure changes before function complains.',
      },
      { kind: 'sub', text: 'Page 2 — Irreversibility' },
      {
        kind: 'p',
        text: 'Fluid volume responds to treatment; ELM and EZ continuity do not reliably recover. A CL 2 versus CL 4 comparison makes the threshold visible. Takeaway: treat the fluid, read the bands.',
      },
      { kind: 'sub', text: 'Page 3 — Proactive management of clusters' },
      {
        kind: 'p',
        text: 'Each phenotype gets a review rhythm — what to watch, at what interval, and what would prompt action. This is what converts recognition into proactive management. Rhythms are explicitly illustrative.',
      },
      { kind: 'sub', text: 'Page 4 — Embed it in the read' },
      {
        kind: 'p',
        text: 'No new equipment, scan or visit: read five variables instead of one, name the phenotype in the record, and set the interval to the phenotype. “What changes on Tuesday” is answered directly.',
      },
      { kind: 'sub', text: 'Page 5 — Differentiation tail · Prevent' },
      {
        kind: 'p',
        text: 'The timing argument becomes the product argument: address the inflammatory arm while the architecture is intact. Brand colours only, no logo lockup, claims flagged as pending medical and regulatory approval.',
      },
      { kind: 'accent', text: 'Snak 2 answers “when”, and then “how do I actually do this”.' },
    ],
  },
  {
    number: '10',
    title: 'Snak 3 — Advanced · Ignite precision',
    blocks: [
      {
        kind: 'p',
        text: 'This reader is already practising precision medicine. Selling the concept back to them is condescending; the offer is infrastructure and participation.',
      },
      { kind: 'sub', text: 'Page 1 — Precision medicine principles' },
      {
        kind: 'p',
        text: 'Right patient, right intervention, right time, measured — each mapped to something the specialist already does. The gap named is instrumentation, not conviction.',
      },
      { kind: 'sub', text: 'Page 2 — AI fit-to-clinic' },
      {
        kind: 'p',
        text: 'An honest does / does-not table. Consistent segmentation and foci counting on one side; diagnosis, therapy choice and self-validation firmly on the other.',
      },
      { kind: 'sub', text: 'Page 3 — Pilot AI in routine practice' },
      {
        kind: 'p',
        text: 'A concrete, finishable pilot: 50 retrospective scans, eight weeks, anonymised under a local data agreement, returning the clinic’s own cluster distribution and inter-reader comparison.',
      },
      { kind: 'sub', text: 'Page 4 — Differentiation tail · Predict' },
      {
        kind: 'p',
        text: 'Openly aspirational. The predictive question is posed as a hypothesis with status chips — not established, no predictive claim, research question — and the full brand lockup appears here for the first time.',
      },
      { kind: 'sub', text: 'Page 5 — Co-create' },
      {
        kind: 'p',
        text: 'The ask is participation rather than conversion: join the pilot, contribute to the Danish dataset, join the peer faculty.',
      },
      {
        kind: 'accent',
        text: 'Precision medicine adoption is not a message. It is a practice.',
      },
    ],
  },
  {
    number: '11',
    title: 'Two delivery modes',
    blocks: [
      { kind: 'sub', text: 'Independent campaigns' },
      {
        kind: 'p',
        text: 'Each instalment stands alone, entered directly from the menu. It opens on its own archetype framing, ends on its own differentiation tail and next step, and never depends on having seen the others.',
      },
      { kind: 'sub', text: 'One long presentation' },
      {
        kind: 'p',
        text: 'Run in sequence for face-to-face use, the three instalments read as a single 15-screen argument with chapter handovers between them and a running screen count for the presenter.',
      },
      {
        kind: 'p',
        text: 'Because branding escalates by phase, the presentation also carries its own compliance logic: the unbranded material can be shown to anyone, and the branded tail only appears once the scientific case has been made.',
      },
    ],
  },
]
